import { useMemo, useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Transaction, Conta } from "@/types/database";
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingUp, PieChart as PieChartIcon, AlertCircle } from 'lucide-react';
import { MonthYearPicker } from '@/components/common/MonthYearPicker';
import { CustomTooltipProps } from "@/types/recharts";
import { getEffectiveTransactionDate } from '@/lib/utils';
import { getCategoryIcon } from "@/constants/categoryIcons";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface TransactionWithCategory extends Transaction {
  categoria?: {
    nome: string;
    cor: string;
    icone: string;
  };
}

interface CategoryIncomeReportProps {
  transactions: TransactionWithCategory[];
  onCategoryClick?: (categoryName: string, selectedMonth: number, selectedYear: number) => void;
  selectedDate?: Date;
}

export function CategoryIncomeReport({
  transactions,
  onCategoryClick,
  selectedDate
}: CategoryIncomeReportProps) {
  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });

  // Helper function to find the latest month with income data
  const getLatestMonthWithIncome = useCallback((transactions: TransactionWithCategory[]) => {
    if (!transactions || transactions.length === 0) {
      return { month: new Date().getMonth(), year: new Date().getFullYear() };
    }
    
    // Filtrar apenas transações válidas e de entrada
    const incomes = transactions.filter(t => {
      if (!t || t.tipo !== 'entrada' || t.ignorar_transacao) return false;
      const date = getEffectiveTransactionDate(t, accounts as Conta[]);
      return date && date instanceof Date && !isNaN(date.getTime());
    });
    
    if (incomes.length === 0) {
      // Fallback para qualquer transação válida se não houver receitas
      const allValid = transactions.filter(t => {
        if (!t || t.ignorar_transacao) return false;
        const date = getEffectiveTransactionDate(t, accounts as Conta[]);
        return date && date instanceof Date && !isNaN(date.getTime());
      });
      
      if (allValid.length > 0) {
        const validDates = allValid.map(t => getEffectiveTransactionDate(t, accounts as Conta[]).getTime());
        const latestDate = new Date(Math.max(...validDates));
        return { 
          month: latestDate.getMonth(), 
          year: latestDate.getFullYear() 
        };
      }
      
      return { month: new Date().getMonth(), year: new Date().getFullYear() };
    }
    
    const validDates = incomes.map(t => getEffectiveTransactionDate(t, accounts as Conta[]).getTime());
    const latestIncome = new Date(Math.max(...validDates));
    return { 
      month: latestIncome.getMonth(), 
      year: latestIncome.getFullYear() 
    };
  }, [accounts]);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Update selected month/year when transactions are loaded or selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      // Use the selectedDate from props when available
      setSelectedMonth(selectedDate.getMonth());
      setSelectedYear(selectedDate.getFullYear());
    } else if (transactions && transactions.length > 0) {
      // Fall back to latest month with income when selectedDate is not provided
      const latestPeriod = getLatestMonthWithIncome(transactions);
      setSelectedMonth(latestPeriod.month);
      setSelectedYear(latestPeriod.year);
    }
  }, [transactions, selectedDate, getLatestMonthWithIncome]);
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const colors = [
    'hsl(142, 76%, 36%)', // Green
    'hsl(221, 83%, 53%)', // Blue
    'hsl(38, 92%, 50%)',  // Orange
    'hsl(262, 83%, 58%)', // Purple
    'hsl(198, 93%, 60%)', // Cyan
    'hsl(84, 81%, 44%)',  // Lime
    'hsl(25, 95%, 53%)',  // Deep Orange
    'hsl(328, 85%, 70%)', // Pink
    'hsl(252, 95%, 85%)', // Light Purple
    'hsl(45, 93%, 47%)',  // Yellow
    'hsl(349, 89%, 60%)'  // Rose
  ];

  const handlePreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(prev => prev + 1);
    } else {
      setSelectedMonth(prev => prev + 1);
    }
  };

  const filteredTransactions = useMemo(() => {
    const monthStart = startOfMonth(new Date(selectedYear, selectedMonth));
    const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth));
    return transactions.filter(t => {
      const transactionDate = getEffectiveTransactionDate(t, accounts as Conta[]);
      return t.tipo === 'entrada' && 
             transactionDate && 
             transactionDate instanceof Date && 
             !isNaN(transactionDate.getTime()) && 
             transactionDate >= monthStart && 
             transactionDate <= monthEnd && 
             !t.ignorar_transacao;
    });
  }, [transactions, selectedMonth, selectedYear, accounts]);

  const incomesByCategory = useMemo(() => {
    const grouped = filteredTransactions.reduce((acc, transaction) => {
      const categoryName = transaction.categoria?.nome || 'Sem categoria';
      const categoryColor = transaction.categoria?.cor || '#6b7280';
      const categoryIcon = transaction.categoria?.icone || 'default';
      if (!acc[categoryName]) {
        acc[categoryName] = {
          name: categoryName,
          color: categoryColor,
          icon: categoryIcon,
          total: 0,
          transactions: []
        };
      }
      acc[categoryName].total += transaction.valor;
      acc[categoryName].transactions.push(transaction);
      return acc;
    }, {} as Record<string, {
      name: string;
      color: string;
      icon: string;
      total: number;
      transactions: TransactionWithCategory[];
    }>);
    return Object.values(grouped).sort((a, b) => b.total - a.total);
  }, [filteredTransactions]);

  const barChartData = incomesByCategory.map((category, index) => ({
      name: category.name,
      value: category.total,
      percentage: incomesByCategory.length > 0 ? category.total / incomesByCategory.reduce((sum, cat) => sum + cat.total, 0) * 100 : 0,
      color: colors[index % colors.length],
      icon: category.icon,
      transactions: category.transactions.length
    }));

  const totalIncome = incomesByCategory.reduce((sum, category) => sum + category.total, 0);

  const insights = useMemo(() => {
    if (incomesByCategory.length === 0) return [];
    const topCategory = incomesByCategory[0];
    const top3Total = incomesByCategory.slice(0, 3).reduce((sum, cat) => sum + cat.total, 0);
    const top3Percentage = totalIncome > 0 ? top3Total / totalIncome * 100 : 0;
    const averagePerCategory = totalIncome / incomesByCategory.length;
    return [{
      icon: TrendingUp,
      text: `Sua maior receita foi "${topCategory.name}" com ${formatCurrency(topCategory.total)}`
    }, {
      icon: PieChartIcon,
      text: `As 3 maiores categorias representam ${top3Percentage.toFixed(1)}% do total`
    }, {
      icon: AlertCircle,
      text: `Média por categoria: ${formatCurrency(averagePerCategory)}`
    }];
  }, [incomesByCategory, totalIncome]);


  const CustomTooltip = ({
    active,
    payload,
    label
  }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-popover-foreground">{label}</p>
          <div className="space-y-1 mt-2">
            <p className="text-sm">
              <span className="text-muted-foreground">Valor:</span>
              <span className="font-semibold ml-1">{formatCurrency(data.value as number)}</span>
            </p>
          </div>
        </div>;
    }
    return null;
  };

  return <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground">
                Total de Receitas por Categoria
              </CardTitle>
          <div className="p-2 rounded-md bg-income text-income-foreground">
            <TrendingUp className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-base font-bold text-income">{formatCurrency(totalIncome)}</div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {incomesByCategory.length} {incomesByCategory.length === 1 ? 'categoria' : 'categorias'}
          </p>
        </CardContent>
      </Card>

      {/* Enhanced Month/Year Navigation */}
       <MonthYearPicker
         selectedDate={new Date(selectedYear, selectedMonth, 1)}
         onDateChange={(date) => {
           setSelectedMonth(date.getMonth());
           setSelectedYear(date.getFullYear());
         }}
       />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Distribuição por Categoria</CardTitle>
        </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {barChartData.map((entry, index) => <div key={entry.name} className="cursor-pointer hover:bg-accent/30 p-2 rounded-lg transition-colors" onClick={() => onCategoryClick?.(entry.name, selectedMonth, selectedYear)}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {(() => {
                        const Icon = getCategoryIcon(entry.icon);
                        return (
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" 
                            style={{ backgroundColor: `${entry.color}20` }}
                          >
                            <Icon className="w-4 h-4" style={{ color: entry.color }} />
                          </div>
                        );
                      })()}
                      <span className="text-sm font-medium truncate">{entry.name}</span>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <div className="text-sm font-bold">{formatCurrency(entry.value)}</div>
                    </div>
                  </div>
                  
                  <div className="relative h-6 bg-accent/20 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300" style={{
                  width: `${Math.max(entry.percentage, 5)}%`,
                  backgroundColor: colors[index % colors.length]
                }}>
                    </div>
                  </div>
                  
                  <div className="mt-1 text-xs text-muted-foreground">
                    {entry.transactions} {entry.transactions === 1 ? 'transação' : 'transações'}
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

      {insights.length > 0 && <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Insights do Período
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
              {insights.map((insight, index) => <div key={index} className="flex items-start gap-3 p-3 bg-accent/20 rounded-lg">
                  <insight.icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground leading-relaxed">{insight.text}</p>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      {incomesByCategory.length === 0 && <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="text-center text-muted-foreground">
              <p className="text-sm sm:text-base">Nenhuma receita encontrada para o período selecionado.</p>
            </div>
          </CardContent>
        </Card>}
    </div>;
}